import React, { useEffect, useRef, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import "./Chapter.css";

import useQueryParams from "../../../../../../hooks/useQueryParams";

import {
  setChoices,
  setFeedback,
  setIsAnsweredCorrectly,
  setIsModalOpen,
  setSelectedBlank,
  setSelectedChoice,
} from "../../../../../../redux/chapter/chapterOptions/chapterOptions";

import {
  fetchChapterData,
  saveBlankProgress,
  updateProgress,
} from "../../../../../../redux/chapter/chapterSlice";

import BottomModalWrapper from "./BottomModalWrapper";

const Chapter = ({ darkMode, onPracticeCompleted }) => {
  const dispatch = useDispatch();

  const containerRef = useRef(null);

  const params = useQueryParams();

  const { chapter_info, user_progress, fetchedFor } = useSelector(
    (state) => state.chapter
  );

  const { isModalOpen } = useSelector((state) => state.options);

  const chapter_id = params.get("chapter_id");

  const read_mode_params = params.get("read_mode");

  // const possibleActions = ["read_mode", "start", "reset_start", "resume"];

  // const actionEntry = possibleActions.find((key) => params.has(key));

  // const value = actionEntry ? params.get(actionEntry) : null;

  useEffect(() => {
    dispatch(setIsModalOpen(false));

    dispatch(setSelectedBlank(null));

    dispatch(setChoices([]));

    dispatch(setSelectedChoice(""));

    dispatch(setFeedback(""));

    dispatch(setIsAnsweredCorrectly(false));
  }, [dispatch]);

  useEffect(() => {
    if (!chapter_id) return;

    const fetchKey = `${chapter_id}`;

    if (fetchedFor === fetchKey) return;

    dispatch(fetchChapterData({ chapter_id }));
  }, [chapter_id, dispatch, fetchedFor]);

  const htmlContent = useMemo(() => {
    if (!chapter_info) return { __html: "" };

    return {
      __html:
        read_mode_params === "1"
          ? chapter_info.read_only_html_content
          : chapter_info.html_content,
    };
  }, [chapter_info, read_mode_params]);

  useEffect(() => {
    const loadMathJax = () => {
      if (window.MathJax) return;

      window.MathJax = {
        tex: {
          inlineMath: [
            ["$", "$"],

            ["\\(", "\\)"],
          ],

          displayMath: [
            ["$$", "$$"],

            ["\\[", "\\]"],
          ],
        },

        svg: { fontCache: "global" },
      };

      const script = document.createElement("script");

      script.type = "text/javascript";

      script.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js";

      script.async = true;

      document.head.appendChild(script);
    };

    loadMathJax();
  }, []);

  useEffect(() => {
    const renderMath = () => {
      if (window.MathJax && window.MathJax.typesetPromise) {
        window.MathJax.typesetPromise([containerRef.current]).catch((err) =>
          console.error("MathJax typeset failed:", err)
        );
      }
    };

    const timer = setTimeout(renderMath, 200);

    return () => clearTimeout(timer);
  }, [htmlContent]);

  useEffect(() => {
    if (
      !containerRef.current ||
      !chapter_info?.html_content ||
      read_mode_params === "1"
    )
      return;

    const timer = setTimeout(() => {
      const blanks = containerRef?.current?.querySelectorAll(".blank-wrap");

      blanks.forEach((blankWrap) => {
        const blank = blankWrap.querySelector(".blank");

        const blankId = parseInt(blank.getAttribute("blank_id"), 10);

        const progress = user_progress.find((p) => p.qn_blank_id === blankId);

        const choiceList =
          blank.getAttribute("data-choices")?.split("@@@") || [];

        blankWrap.classList.remove("correct", "incorrect", "unattempted");

        if (progress && progress.selected_option_index) {
          const selectedChoice = choiceList[progress.selected_option_index - 1];

          blank.textContent = selectedChoice || "---------";

          blankWrap.classList.add(
            progress.is_correct ? "correct" : "incorrect"
          );
        } else {
          blank.textContent = "---------";

          blankWrap.classList.add("unattempted");
        }

        const clearButton = blankWrap.querySelector(".blank-x");

        const chapterId = chapter_info?.chapter_id;

        if (clearButton && blank.textContent.trim() !== "---------") {
          clearButton._clearHandler = (e) => {
            e.stopPropagation();

            blank.textContent = "---------";

            blankWrap.classList.remove("correct", "incorrect");

            blankWrap.classList.add("unattempted");

            dispatch(
              updateProgress({
                qn_blank_id: blankId,

                is_correct: false,

                selected_option_index: null,
              })
            );

            dispatch(
              saveBlankProgress({
                chapter_id: chapterId,

                qn_blank_id: blankId,

                is_correct: false,

                selected_option_index: 0,

                user_id: chapter_info?.user_id,
              })
            );
          };

          if (clearButton._existingClearHandler) {
            clearButton.removeEventListener(
              "click",

              clearButton._existingClearHandler
            );
          }

          clearButton._existingClearHandler = clearButton._clearHandler;

          clearButton.addEventListener("click", clearButton._clearHandler);
        }
      });
    }, 200);

    return () => {
      const blanks = containerRef.current?.querySelectorAll(".blank-wrap");

      blanks?.forEach((blankWrap) => {
        const clearButton = blankWrap.querySelector(".blank-x");

        if (clearButton && clearButton._existingClearHandler) {
          clearButton.removeEventListener(
            "click",

            clearButton._existingClearHandler
          );
        }
      });

      clearTimeout(timer);
    };
  }, [chapter_info, user_progress, dispatch, read_mode_params]);

  useEffect(() => {
    if (
      !containerRef.current ||
      !chapter_info?.html_content ||
      read_mode_params === "1"
    )
      return;

    const blanks = containerRef.current.querySelectorAll(".blank-wrap");

    const handleBlankClick = (blankWrap) => {
      const blank = blankWrap.querySelector(".blank");

      const blankId = blank.getAttribute("blank_id");

      const choiceList = blank.getAttribute("data-choices")?.split("@@@") || [];

      dispatch(setSelectedBlank(blankId));

      dispatch(setChoices(choiceList));

      const progress = user_progress.find(
        (p) => p.qn_blank_id === parseInt(blankId, 10)
      );

      const selectedChoice = progress?.selected_option_index
        ? choiceList[progress.selected_option_index - 1]
        : "";

      dispatch(setSelectedChoice(selectedChoice));

      dispatch(setFeedback(""));

      dispatch(setIsAnsweredCorrectly(progress?.is_correct === 1 || false));

      dispatch(setIsModalOpen(true));
    };

    blanks.forEach((blankWrap) => {
      blankWrap._clickHandler = () => handleBlankClick(blankWrap);

      blankWrap.addEventListener("click", blankWrap._clickHandler);
    });

    return () => {
      blanks.forEach((blankWrap) => {
        if (blankWrap._clickHandler)
          blankWrap.removeEventListener("click", blankWrap._clickHandler);
      });
    };
  }, [chapter_info, user_progress, dispatch, read_mode_params]);

  return (
    <div className={`html-container ${darkMode ? "dark" : "light"}`}>
      <div
        ref={containerRef}
        className="html-content"
        dangerouslySetInnerHTML={htmlContent}
      />

      {read_mode_params !== "1" && isModalOpen && (
        <BottomModalWrapper
          isModalOpen={isModalOpen}
          containerRef={containerRef}
          chapterInfo={chapter_info}
          darkMode={darkMode}
          onAction={({ type }) => {
            if (type === "completed") {
              if (onPracticeCompleted) {
                onPracticeCompleted();
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default Chapter;
